import {useVoiceDictation} from 'app/pages/chatbot/components/hooks/useVoiceDictation'
import {Mic, Send, Square, X} from 'lucide-react'
import React, {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'

type Props = {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  placeholder: string
  disabled?: boolean
  /** Server flow: a low-confidence transcript is marked for checking (handoff §6.9). */
  flagLowConfidence?: boolean
}

const formatTimer = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

/**
 * Versi's composer (handover, 4C "Voice input"). The microphone records an instruction for
 * transcription — not a live voice call: a recording indicator with a timer, Stop and Cancel.
 * Stop puts the transcript in the editable composer; Cancel discards the recording. Nothing is
 * sent until the user presses Send.
 */
const VersiComposer: FC<Props> = ({
  value,
  onChange,
  onSend,
  placeholder,
  disabled,
  flagLowConfidence,
}) => {
  const intl = useIntl()
  const t = (id: string) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.TEACHER_ALLOCATION.AI.${id}`})
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const valueRef = useRef(value)
  valueRef.current = value
  const [seconds, setSeconds] = useState(0)
  const [voiceError, setVoiceError] = useState<string | null>(null)
  const [checkTranscript, setCheckTranscript] = useState(false)

  const dictation = useVoiceDictation({
    onTranscribed: (text, meta) => {
      if (flagLowConfidence) setCheckTranscript(Boolean(meta?.lowConfidence))
      const current = valueRef.current.trim()
      onChange(current ? `${current} ${text.trim()}` : text.trim())
      requestAnimationFrame(() => textareaRef.current?.focus())
    },
    onError: (errorKey) => setVoiceError(errorKey),
  })

  useEffect(() => {
    if (!dictation.isRecording) {
      setSeconds(0)
      return
    }
    const started = Date.now()
    const timer = window.setInterval(
      () => setSeconds(Math.floor((Date.now() - started) / 1000)),
      250
    )
    return () => window.clearInterval(timer)
  }, [dictation.isRecording])

  // Grow with the text, up to a few lines.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`
  }, [value])

  const canSend = !disabled && value.trim().length > 0 && !dictation.isRecording

  return (
    <div className='ta-composer'>
      {dictation.isRecording ? (
        <div className='ta-composer__recording' role='status' aria-live='polite'>
          <span className='ta-composer__rec-dot' aria-hidden />
          <span>{t('RECORDING')}</span>
          <span className='ta-composer__timer'>{formatTimer(seconds)}</span>
          <div className='ta-composer__rec-actions'>
            <button
              type='button'
              className='ta-btn ta-btn--ghost ta-btn--sm'
              onClick={dictation.cancel}
            >
              <X size={15} aria-hidden />
              {t('RECORDING_CANCEL')}
            </button>
            <button
              type='button'
              className='ta-btn ta-btn--primary ta-btn--sm'
              onClick={dictation.stop}
            >
              <Square size={13} aria-hidden />
              {t('RECORDING_STOP')}
            </button>
          </div>
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          placeholder={dictation.isTranscribing ? t('TRANSCRIBING') : placeholder}
          aria-label={placeholder}
          disabled={dictation.isTranscribing}
          onChange={(e) => {
            if (checkTranscript && !e.target.value.trim()) setCheckTranscript(false)
            onChange(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              if (canSend) onSend()
            }
          }}
        />
      )}
      {!dictation.isRecording && (
        <div className='ta-composer__buttons'>
          {dictation.isSupported && (
            <button
              type='button'
              className='ta-composer__mic'
              aria-label={t('MIC')}
              disabled={dictation.isTranscribing}
              onClick={() => {
                setVoiceError(null)
                dictation.start()
              }}
            >
              {dictation.isTranscribing ? (
                <span className='spinner-border spinner-border-sm' aria-hidden />
              ) : (
                <Mic size={20} aria-hidden />
              )}
            </button>
          )}
          <button
            type='button'
            className='ta-composer__send'
            aria-label={t('SEND')}
            disabled={!canSend}
            onClick={onSend}
          >
            <Send size={20} aria-hidden />
          </button>
        </div>
      )}
      {checkTranscript && value.trim() && (
        <p className='ta-composer__check' role='status'>
          {t('VOICE_CHECK')}
        </p>
      )}
      {voiceError && (
        <p className='ta-composer__error' role='alert'>
          {intl.formatMessage({id: voiceError})}
        </p>
      )}
    </div>
  )
}

export default VersiComposer
